import { useEffect } from 'react';
import { Button } from 'react-bootstrap';
import { createPortal } from 'react-dom';
import { FaBars, FaChartLine, FaColumns, FaMapMarkedAlt } from 'react-icons/fa';
import { MdClose } from 'react-icons/md';
import { Group, Panel } from 'react-resizable-panels';
import { ResizablePanelHandle } from 'utils/layout/panelResize.utils';

import expandedViewConfig from '../../config/expandedView.config';

const EXPANDED_LAYOUT_OPTIONS = [
  { key: 'stack', label: 'Stacked view', icon: <FaBars size={16} /> },
  { key: 'split', label: 'Left right view', icon: <FaColumns size={16} /> },
  { key: 'map', label: 'Map only', icon: <FaMapMarkedAlt size={16} /> },
  { key: 'graph', label: 'Graph only', icon: <FaChartLine size={16} /> },
];

const ExpandedPanel = ({ children, splitDirection }) => (
  <Panel
    defaultSize={50}
    minSize={20}
    className={`flex-fill min-w-0 min-h-0 p-2 overflow-hidden ${
      splitDirection === 'horizontal' ? 'h-100' : 'w-100'
    }`}
  >
    {children}
  </Panel>
);

const ConnectedDevicesExpandedView = ({
  open,
  layout,
  setLayout,
  onClose,
  renderMapPanel,
  renderGraphPanel,
}) => {
  useEffect(() => {
    if (!open || typeof window === 'undefined') return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose, open]);

  if (!open || typeof document === 'undefined') return null;

  const mapPanel = renderMapPanel({ expanded: true });

  const renderContent = () => {
    if (layout === 'map') {
      return (
        <section className="flex-fill min-w-0 min-h-0 p-2 overflow-hidden w-100 h-100">
          {mapPanel}
        </section>
      );
    }

    if (layout === 'graph') {
      const graphPanel = renderGraphPanel({
        constrainControls: true,
        expanded: true,
        graphControlsAlignment: 'end',
      });

      return (
        <section className="flex-fill min-w-0 min-h-0 p-2 overflow-hidden w-100 h-100">
          {graphPanel}
        </section>
      );
    }

    if (layout === 'split') {
      const graphPanel = renderGraphPanel({
        constrainControls: true,
        expanded: true,
        graphControlsAlignment: 'split',
      });

      return (
        <Group
          orientation="horizontal"
          className="min-w-0 min-h-0"
          style={{ minHeight: 0, height: '100%', width: '100%' }}
        >
          <ExpandedPanel splitDirection="horizontal">{mapPanel}</ExpandedPanel>
          <ResizablePanelHandle direction="vertical" />
          <ExpandedPanel splitDirection="horizontal">{graphPanel}</ExpandedPanel>
        </Group>
      );
    }

    const graphPanel = renderGraphPanel({
      constrainControls: true,
      expanded: true,
      graphControlsAlignment: 'end',
    });

    return (
      <Group
        orientation="vertical"
        className="min-w-0 min-h-0"
        style={{ minHeight: 0, height: '100%', width: '100%' }}
      >
        <ExpandedPanel splitDirection="vertical">{mapPanel}</ExpandedPanel>
        <ResizablePanelHandle direction="horizontal" />
        <ExpandedPanel splitDirection="vertical">{graphPanel}</ExpandedPanel>
      </Group>
    );
  };

  return createPortal(
    <div
      className="connected-devices-expanded-overlay position-fixed top-0 start-0 w-100 h-100 d-flex flex-column min-w-0 min-h-0"
      style={{ zIndex: 'var(--z-floating)', background: 'var(--bs-body-bg)' }}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="d-flex align-items-center justify-content-end gap-2 p-2 flex-shrink-0"
        aria-label="Expanded view layout"
      >
        <div className="d-flex align-items-center gap-1">
          {EXPANDED_LAYOUT_OPTIONS.map((option) => (
            <Button
              key={option.key}
              variant={layout === option.key ? 'primary' : 'secondary'}
              className="connected-devices-icon-button bg-primary border-primary text-white p-0 d-grid align-items-center justify-content-center"
              style={expandedViewConfig.buttonStyle}
              onClick={() => setLayout(option.key)}
              aria-label={option.label}
              title={option.label}
            >
              {option.icon}
            </Button>
          ))}
        </div>
        <Button
          variant="primary"
          className="connected-devices-icon-button bg-primary border-primary text-white p-0 d-grid align-items-center justify-content-center"
          style={expandedViewConfig.buttonStyle}
          onClick={onClose}
          aria-label="Collapse map and graph view"
          title="Collapse map and graph view"
        >
          <MdClose size={24} />
        </Button>
      </div>
      <div
        className={`flex-fill min-w-0 min-h-0 d-flex w-100 ${
          layout === 'stack' ? 'flex-column' : 'flex-row'
        } connected-devices-expanded-content-${layout}`}
      >
        {renderContent()}
      </div>
    </div>,
    document.body,
  );
};

export default ConnectedDevicesExpandedView;
